import { useEffect, useId, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  CHECKPOINT_METHOD_OPTIONS,
  DEFAULT_GPS_GEOFENCE_RADIUS_METERS,
  checkpointMethodHint,
  checkpointMintPrimaryLabel,
  checkpointUsesPhysicalTags,
  formatCheckpointMethod,
} from "../lib/checkpoint-methods";
import {
  addCheckpointToRoute,
  archiveCheckpoint,
  CHECKPOINT_SETUP_LIST_LIMIT,
  checkpointDraftFromRow,
  checkpointLocationLabel,
  createCheckpoint,
  fetchCheckpointMaterialStatus,
  fetchCheckpointSetupList,
  fetchPlacementCheckpoints,
  placementProvenanceLabel,
  promoteCheckpointPlacement,
  removeCheckpointFromRoute,
  restoreCheckpoint,
  routesForCheckpoint,
  SAFE_CHECKPOINT_WRITE_FAILURE,
  splitPlacementQueues,
  updateCheckpoint,
  updateRouteCheckpoint,
  type CheckpointMaterialStatusRow,
  type CheckpointRestoreOutcome,
  type CheckpointSetupRow,
  type PlacementCheckpointRow,
} from "../data/checkpoint-setup";
import {
  formatUnroutedMaterialBadge,
  unroutedMaterialState,
  type UnroutedMaterialState,
} from "../data/checkpoint-material";
import {
  convertCheckpointRequest,
  fetchCheckpointRequests,
  formatCheckpointRequestRelativeTime,
  rejectCheckpointRequest,
  SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE,
  type CheckpointRequestConversion,
  type CheckpointRequestRow,
  type CheckpointRequestConvertMode,
} from "../data/checkpoint-requests";
import { fetchEditableRoutes, type EditableRoute } from "../data/routes";
import { fetchSiteAssets, ASSET_TYPE_LABEL, type SiteAsset } from "../data/site-asset";
import type { ManagedSite } from "../data/clients-sites";
import {
  collectOccupiedMapCoords,
  formatGeocodeQuery,
  geocodeAddressWithNudge,
} from "../lib/geocode";
import { useReadModel } from "../data/useReadModel";
import { confirmDiscardIfDirty } from "../components/confirm-discard";
import { GlassDrawer, GlassDrawerFooter } from "../components/GlassDrawer";
import { GlassModal } from "../components/GlassModal";
import { useTokenMintSession, type TokenMintSessionContextValue } from "./token-mint-session";

// Checkpoint editor (CONSOLE-SETUP-CHECKPOINT-EDITOR). Reads: the hosted setup view +
// active routes (so zero-checkpoint routes still get an Add affordance - the view alone is
// route_checkpoint-rooted and cannot show them) + active L1 asset zones for the AC-001Y.9
// picker (best-effort: a zone read failure degrades to "None" and never blocks the editor)
// + the PRD-001I placement reader (RLS checkpoints table; pins without a route are
// structurally absent from the view, so walk drafts AND unrouted confirmed pins arrive
// only through it).
// Create is CREATE-THEN-ATTACH in one
// explicit submit (Codex v1 ruling: a checkpoint not on a route is not actionable for
// patrol). Edit round-trips EVERY advanced field verbatim via checkpointDraftFromRow -
// the update contract has no partials. Reorder swaps sort orders with the neighbor through
// two updates that carry each row's own attachment fields. Remove (from route) and Archive
// (checkpoint itself; disable-not-delete) sit behind consequence confirms.
// Token lifecycle: mint_checkpoint_token is route-independent. QR/NFC material mints and
// replaces here in Setup for BOTH unrouted pins and routed checkpoints (so operators do not
// have to leave the list they just edited). Patrols still owns bulk route "Mint kit" plus
// readiness recovery. GPS/manual methods never grow mint buttons. Without a provider or
// tokens.mint, every mint affordance fails closed to invisible.
// Material state (Wave A2, Q4a): counts come from management_checkpoint_material_status,
// which is checkpoint-rooted so unrouted pins finally have an answer. Material on file ->
// badge plus Replace only. None -> Mint only. View unavailable -> BOTH, with the confirm
// saying the state is unknown; the server stays the arbiter in every case, and no token body
// or human code is ever re-shown.
// Route attach (Wave A1, Q2a/Q3a): a CONFIRMED unrouted pin joins an existing route through
// the same add_checkpoint_to_route RPC create-then-attach already uses - no second pin, no
// new RPC. Two doors onto it: "Add to route..." on the unrouted row, and Existing-vs-New on
// a route's Add. Walk drafts get no attach door at all because the RPC refuses provisional
// pins; promote is their next step.
// Retire (2026-08-20 field pin lifecycle lock): every row on this surface can be retired, not
// just the routed ones. A pin planted in the field and then judged not real used to have Promote
// and Mint as its only doors, so bad drafts accumulated with nowhere to go. Retire is the same
// archive_checkpoint call the routed row has always used - soft, audited, restorable, history
// kept - and a retired unrouted pin then leaves this surface AND the CAD map, because both read
// active pins only. There is still no delete.
// Viewers without schedule.manage get the read-only grouped list.

interface RouteGroup {
  route: EditableRoute;
  rows: CheckpointSetupRow[];
}

type Dialog =
  | { kind: "create"; route: EditableRoute; maxOrder: number }
  | { kind: "edit"; row: CheckpointSetupRow }
  | { kind: "remove"; row: CheckpointSetupRow }
  | { kind: "retire"; row: CheckpointSetupRow }
  | { kind: "restore"; row: CheckpointSetupRow }
  | { kind: "promote"; draft: PlacementCheckpointRow }
  /**
   * Retire for a pin the route-rooted view cannot see: a walk draft or a confirmed pin with no
   * route. Deliberately a SEPARATE kind from "retire", which carries a CheckpointSetupRow and
   * owns the shipped routed path. Same RPC, same confirm component, different carrier.
   */
  | { kind: "retire-pin"; pin: PlacementCheckpointRow }
  | { kind: "attach"; pin: PlacementCheckpointRow }
  | { kind: "reject-proposal"; proposal: CheckpointRequestRow }
  | { kind: "convert-proposal"; proposal: CheckpointRequestRow };

function focusedRouteId(dialog: Dialog | null): string | null {
  if (dialog === null) return null;
  if (dialog.kind === "create") return dialog.route.routeId;
  if (
    dialog.kind === "promote" ||
    dialog.kind === "retire-pin" ||
    dialog.kind === "attach" ||
    dialog.kind === "reject-proposal" ||
    dialog.kind === "convert-proposal"
  ) {
    return null;
  }
  return dialog.row.routeId;
}

function focusedCheckpointId(dialog: Dialog | null): string | null {
  if (dialog === null) return null;
  if (
    dialog.kind === "create" ||
    dialog.kind === "promote" ||
    dialog.kind === "retire-pin" ||
    dialog.kind === "attach" ||
    dialog.kind === "reject-proposal" ||
    dialog.kind === "convert-proposal"
  ) {
    return null;
  }
  return dialog.row.routeCheckpointId;
}

/** Next free sort order on a route (the RPC takes an explicit position; it does not append). */
function nextSortOrder(rows: readonly CheckpointSetupRow[], routeId: string): number {
  return (
    Math.max(0, ...rows.filter((row) => row.routeId === routeId).map((row) => row.sortOrder)) + 1
  );
}

export function CheckpointEditor({
  client,
  accountId,
  canManage = false,
  canMint = false,
  mapTilerKey,
  pickerRoutes,
  managedSites = [],
}: {
  client: SupabaseClient;
  accountId: string;
  /** schedule.manage - write affordances only exist when true (fail closed). */
  canManage?: boolean;
  /**
   * tokens.mint - tag mint affordances (unrouted + routed QR/NFC) need this AND a mounted
   * TokenMintSessionProvider; either one missing fails closed to invisible.
   */
  canMint?: boolean;
  /** Publishable MapTiler key for address → coordinate lookup (optional). */
  mapTilerKey?: string | undefined;
  /** Shared active routes from Setup (stays in sync when routes change above). */
  pickerRoutes?: EditableRoute[];
  /** Sites from Setup for address prefill and coordinate de-duplication. */
  managedSites?: readonly ManagedSite[];
}) {
  const mintSession = useTokenMintSession();
  const mintReady = canMint && mintSession?.enabled === true;
  const mintGeneration = mintSession?.mintGeneration ?? 0;
  const pickerRoutesKey = useMemo(
    () => pickerRoutes?.map((route) => route.routeId).join("|") ?? "",
    [pickerRoutes],
  );
  const { status, data, error, reload, lastRefreshedAt } = useReadModel<{
    routes: EditableRoute[];
    rows: CheckpointSetupRow[];
    assets: SiteAsset[];
    placements: PlacementCheckpointRow[];
    proposals: CheckpointRequestRow[];
    material: Map<string, CheckpointMaterialStatusRow>;
  }>(
    async () => {
      // PRD-001I unrouted pins (walk drafts + confirmed pins without a route) live only on the
      // checkpoints table (the setup view is route-rooted). Sharing the loader keeps one reload
      // path, so a promote refreshes both queues and the routed list together.
      const [rows, routes, assets, placements, proposals, material] = await Promise.all([
        fetchCheckpointSetupList(client, accountId),
        pickerRoutes ??
          fetchEditableRoutes(client, accountId).then((editableRoutes) =>
            editableRoutes.filter((route) => route.active),
          ),
        // Asset zones are optional context; the checkpoint editor must still work without them.
        fetchSiteAssets(client, accountId).catch(() => []),
        fetchPlacementCheckpoints(client, accountId),
        // The manager queue is schedule.manage scoped. Viewers never issue the manager RPC.
        canManage ? fetchCheckpointRequests(client, accountId) : Promise.resolve([]),
        // Wave A2 material counts. Only worth reading when a mint surface can act on them;
        // without tokens.mint the badge would be decoration a viewer cannot use. Already
        // fails soft to an empty map, which reads as "unknown" downstream.
        mintReady
          ? fetchCheckpointMaterialStatus(client, accountId)
          : Promise.resolve(new Map<string, CheckpointMaterialStatusRow>()),
      ]);
      return { routes, rows, assets, placements, proposals, material };
    },
    // mintGeneration: a mint in this tab changes material counts, so the badge and the
    // Mint-vs-Replace split must re-derive instead of showing the pre-mint state.
    [client, accountId, canManage, mintReady, mintGeneration, pickerRoutes, pickerRoutesKey],
  );
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  /** Q2a: a route's Add offers an existing unrouted pin as well as a brand new checkpoint. */
  const [addMode, setAddMode] = useState<"existing" | "new">("new");
  /**
   * True between a failed attach and the refresh that follows it. reload() is fire and forget
   * (it bumps a nonce), so without this the form is interactive again the instant the failure
   * renders, and a fast second click would recompute the position from the SAME stale rows and
   * collide again. Gating the retry on the refresh is what makes it a real retry.
   */
  const [refreshing, setRefreshing] = useState(false);
  /**
   * PRD-001 checkpoint Setup first-class, lock 10: retire shows route impact and changes nothing
   * by default. This starts false on every open and is reset when the dialog closes, so the opt
   * in can never be carried from one checkpoint to the next.
   */
  const [removeFromRoutes, setRemoveFromRoutes] = useState(false);

  useEffect(() => {
    setRefreshing(false);
  }, [lastRefreshedAt]);

  const drawerDialogKey =
    dialog !== null && (dialog.kind === "create" || dialog.kind === "edit")
      ? dialog.kind === "edit"
        ? `edit-checkpoint:${dialog.row.routeCheckpointId}`
        : `create-checkpoint:${dialog.route.routeId}`
      : null;

  // The new-checkpoint form unmounts when the operator switches to the existing-pin path, so
  // its unsaved text is already gone; keeping dirty true would fire a discard prompt over
  // nothing.
  useEffect(() => {
    setFormDirty(false);
  }, [drawerDialogKey, addMode]);

  function clearDrawer(): void {
    setDialog(null);
    setFailure(null);
    setFormDirty(false);
  }

  function requestDrawerClose(): void {
    if (busy) return;
    if (!confirmDiscardIfDirty(formDirty)) return;
    clearDrawer();
  }

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading checkpoints...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load checkpoint setup."}
      </p>
    );
  }

  const { routes, rows, assets, placements, proposals, material } = data ?? {
    routes: [],
    rows: [],
    assets: [],
    placements: [],
    proposals: [],
    material: new Map<string, CheckpointMaterialStatusRow>(),
  };
  const siteNameById = new Map(managedSites.map((entry) => [entry.siteId, entry.name]));
  const routedCheckpointIds = new Set(rows.map((row) => row.checkpointId));
  /**
   * True when the route-rooted read came back full, which means routedCheckpointIds and
   * routesForCheckpoint are both working from a partial picture: the setup list is capped and
   * ordered by sort_order, so the dropped rows are the tail stops of every route. A checkpoint
   * whose only links were dropped then looks unrouted here and lands in the queues below.
   *
   * Nothing can be inferred safely from that, so the retire confirms are told to stop asserting
   * route impact instead of quietly getting it wrong. A malformed row skipped during mapping
   * would make this read one short and under-report, which fails toward the shipped wording
   * rather than toward a new claim.
   */
  const routeImpactUnknown = rows.length >= CHECKPOINT_SETUP_LIST_LIMIT;
  const { walkDrafts, unroutedConfirmed } = splitPlacementQueues(placements, routedCheckpointIds);
  // A1: only ACTIVE routes on the pin's own site can take it - the RPC refuses a site mismatch
  // and an archived route, so offering either would be a button that only ever fails.
  function attachRouteOptions(pin: PlacementCheckpointRow): EditableRoute[] {
    return routes.filter((route) => route.active && route.siteId === pin.siteId);
  }
  // A1 mirror: candidates for a route's Existing-pin path. Confirmed only (walkDrafts are
  // excluded upstream by splitPlacementQueues) and same-site only, for the same reason.
  function attachPinOptions(route: EditableRoute): PlacementCheckpointRow[] {
    return unroutedConfirmed.filter((pin) => pin.siteId === route.siteId);
  }
  const groups: RouteGroup[] = routes.map((route) => ({
    route,
    rows: rows
      .filter((row) => row.routeId === route.routeId)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }));
  const activeRouteId = focusedRouteId(dialog);
  const activeCheckpointId = focusedCheckpointId(dialog);
  const formSiteId =
    dialog !== null && (dialog.kind === "create" || dialog.kind === "edit")
      ? dialog.kind === "create"
        ? dialog.route.siteId
        : dialog.row.siteId
      : null;
  const formSite =
    formSiteId !== null ? managedSites.find((entry) => entry.siteId === formSiteId) ?? null : null;
  const formOccupiedCoords =
    formSiteId !== null
      ? collectOccupiedMapCoords(
          formSite,
          rows.filter((row) => row.siteId === formSiteId),
          dialog?.kind === "edit" ? dialog.row.checkpointId : null,
        )
      : [];
  // Only zones on the checkpoint's own site are linkable - the RPC rejects anything else.
  const formSiteAssets =
    formSiteId !== null ? assets.filter((asset) => asset.siteId === formSiteId) : [];

  async function run(
    action: () => Promise<{ ok: boolean; reason?: string; partial?: string }>,
    options?: { successMessage?: string; fallbackFailure?: string },
  ): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setFailure(null);
    setSuccess(null);
    try {
      const outcome = await action();
      if (outcome.ok) {
        setDialog(null);
        // `partial` wins: an action that only half-landed must not be reported with the caller's
        // optimistic sentence.
        setSuccess(outcome.partial ?? options?.successMessage ?? null);
        reload();
        return true;
      }
      setFailure((outcome as { reason: string }).reason);
      return false;
    } catch {
      setFailure(options?.fallbackFailure ?? SAFE_CHECKPOINT_WRITE_FAILURE);
      return false;
    } finally {
      setBusy(false);
    }
  }

  /**
   * A1 attach. Same RPC create-then-attach already uses, so there is exactly one server
   * contract for "checkpoint joins route" and the pin is never re-created. The server does the
   * real gating (schedule.manage, same site, active route, confirmed placement); this only
   * avoids offering doors it would slam.
   */
  async function attachPinToRoute(
    pin: PlacementCheckpointRow,
    route: EditableRoute,
    isRequired: boolean,
  ): Promise<boolean> {
    const attached = await run(
      () =>
        addCheckpointToRoute(client, accountId, route.routeId, pin.checkpointId, {
          sortOrder: nextSortOrder(rows, route.routeId),
          isRequired,
          expectedOffsetSeconds: null,
          windowStartsSeconds: null,
          windowEndsSeconds: null,
        }),
      { successMessage: `${pin.checkpointName} added to ${route.name}.` },
    );
    if (!attached) {
      // route_checkpoints is unique on (account_id, route_id, sort_order) and on
      // (account_id, route_id, checkpoint_id). Both violations arrive as non-allowlisted
      // Postgres errors, so they correctly collapse to the safe generic - but the position
      // came from this tab's cached rows, so retrying against the same stale cache would
      // fail forever. Refreshing on failure makes the retry meaningful instead of a loop,
      // and `refreshing` holds the retry closed until the fresh rows actually land.
      setRefreshing(true);
      reload();
    }
    return attached;
  }

  /**
   * The retire door for a pin the route-rooted view cannot see. Both unrouted queues need the
   * identical control, so it is written once: two copies drift, and the one that drifts is
   * always the queue nobody demos.
   */
  function retirePinButton(pin: PlacementCheckpointRow) {
    return (
      <button
        type="button"
        className="builder__cancel"
        aria-label={`Retire - ${pin.checkpointName}`}
        disabled={busy}
        onClick={() => {
          setFailure(null);
          setSuccess(null);
          setRemoveFromRoutes(false);
          setDialog({ kind: "retire-pin", pin });
        }}
      >
        Retire...
      </button>
    );
  }

  async function moveRow(group: RouteGroup, index: number, delta: -1 | 1): Promise<void> {
    const here = group.rows[index];
    const there = group.rows[index + delta];
    if (here === undefined || there === undefined || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const first = await updateRouteCheckpoint(client, accountId, here.routeCheckpointId, {
        sortOrder: there.sortOrder,
        isRequired: here.isRequired,
        expectedOffsetSeconds: here.expectedOffsetSeconds,
        windowStartsSeconds: here.windowStartsSeconds,
        windowEndsSeconds: here.windowEndsSeconds,
      });
      const second = await updateRouteCheckpoint(client, accountId, there.routeCheckpointId, {
        sortOrder: here.sortOrder,
        isRequired: there.isRequired,
        expectedOffsetSeconds: there.expectedOffsetSeconds,
        windowStartsSeconds: there.windowStartsSeconds,
        windowEndsSeconds: there.windowEndsSeconds,
      });
      if (first.ok && second.ok) {
        reload();
      } else {
        setFailure(!first.ok ? (first as { reason: string }).reason : (second as { reason: string }).reason);
      }
    } catch {
      setFailure(SAFE_CHECKPOINT_WRITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ckedit">
      {failure !== null && dialog === null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {success !== null && dialog === null ? (
        <p className="surface__state ckrequest__success" role="status">
          {success}
        </p>
      ) : null}
      {walkDrafts.length > 0 ? (
        <section
          className="ckedit__group ckedit__group--drafts"
          role="region"
          aria-label="Field walk drafts"
        >
          <div className="ckedit__head">
            <span className="ckedit__route">Field walk drafts</span>
            <span className="ckedit__site">
              {walkDrafts.length === 1 ? "1 provisional pin" : `${walkDrafts.length} provisional pins`}
            </span>
          </div>
          <p className="page__note">
            {canManage
              ? mintReady
                ? "Provisional pins from mobile Setup walks and converted Guard proposals. Promote to confirm a placement. QR and NFC tags can mint right here before the pin joins a route; adding it to a patrol route stays a separate step."
                : "Provisional pins from mobile Setup walks and converted Guard proposals. Promote to confirm a placement. Adding the checkpoint to a patrol route stays a separate step."
              : "Provisional field placement pins awaiting promotion (read-only)."}
          </p>
          <ul className="ckedit__rows">
            {walkDrafts.map((draft) => (
              <li key={draft.checkpointId} className="ckedit__row ckedit__row--draft">
                <span className="ckedit__name">
                  {draft.checkpointName}{" "}
                  <span className="schedule__chip schedule__chip--partial">Draft</span>
                </span>
                <span className="ckedit__context">
                  {siteNameById.get(draft.siteId) ?? "Unknown site"}
                </span>
                <span className="ckedit__coords">
                  {draft.expectedLat !== null && draft.expectedLng !== null
                    ? `${draft.expectedLat.toFixed(4)}, ${draft.expectedLng.toFixed(4)}`
                    : "no coordinates"}
                </span>
                <span className="ckedit__context">{placementProvenanceLabel(draft)}</span>
                {mintReady ? (
                  <MaterialStatusBadge counts={material.get(draft.checkpointId)} />
                ) : null}
                {canManage || mintReady ? (
                  <span className="ckedit__actions">
                    {canManage ? (
                      <button
                        type="button"
                        className="minter__confirm-go"
                        aria-label={`Promote placement - ${draft.checkpointName}`}
                        disabled={busy}
                        onClick={() => {
                          setFailure(null);
                          setDialog({ kind: "promote", draft });
                        }}
                      >
                        Promote
                      </button>
                    ) : null}
                    {canManage ? retirePinButton(draft) : null}
                    {mintReady && mintSession !== null ? (
                      <UnroutedMintActions
                        pin={draft}
                        siteName={siteNameById.get(draft.siteId) ?? null}
                        session={mintSession}
                        counts={material.get(draft.checkpointId)}
                        busy={busy}
                      />
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {canManage ? (
        <section
          className="ckedit__group ckedit__group--proposals"
          role="region"
          aria-label="Guard proposals"
        >
          <div className="ckedit__head">
            <span className="ckedit__route">Guard proposals</span>
            <span className="ckedit__site">
              {proposals.length === 1 ? "1 open proposal" : `${proposals.length} open proposals`}
            </span>
          </div>
          <p className="page__note">
            Guard-submitted checkpoint locations awaiting manager review. Converting creates a
            provisional pin; promotion stays in Field walk drafts above.
          </p>
          {proposals.length === 0 ? (
            <p className="surface__state" role="status">No open guard proposals.</p>
          ) : (
            <ul className="ckedit__rows">
              {proposals.map((proposal) => (
                <li key={proposal.requestId} className="ckedit__row ckedit__row--proposal">
                  <span className="ckedit__name">
                    {proposal.proposedName}{" "}
                    <span className="schedule__chip schedule__chip--credential">Guard proposal</span>
                  </span>
                  <span className="ckedit__context">{proposal.siteName}</span>
                  <time className="ckedit__context" dateTime={proposal.createdAt ?? undefined}>
                    {formatCheckpointRequestRelativeTime(proposal.createdAt)}
                  </time>
                  <span className="ckedit__coords">
                    GPS accuracy {proposal.captureAccuracyMeters} m
                  </span>
                  {proposal.accuracyWarning ? (
                    <span className="schedule__chip schedule__chip--partial">Review accuracy</span>
                  ) : null}
                  {proposal.notePreview !== null ? (
                    <span className="ckedit__proposal-note">
                      Note: {proposal.notePreview}
                    </span>
                  ) : null}
                  <span className="ckedit__actions">
                    <button
                      type="button"
                      className="builder__navbtn"
                      disabled={busy}
                      onClick={() => {
                        setFailure(null);
                        setSuccess(null);
                        setDialog({ kind: "reject-proposal", proposal });
                      }}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      className="minter__confirm-go"
                      disabled={busy}
                      onClick={() => {
                        setFailure(null);
                        setSuccess(null);
                        setDialog({ kind: "convert-proposal", proposal });
                      }}
                    >
                      Convert
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
      {unroutedConfirmed.length > 0 ? (
        <section
          className="ckedit__group ckedit__group--unrouted"
          role="region"
          aria-label="Unrouted checkpoints"
        >
          <div className="ckedit__head">
            <span className="ckedit__route">Unrouted checkpoints</span>
            <span className="ckedit__site">
              {unroutedConfirmed.length === 1
                ? "1 confirmed pin"
                : `${unroutedConfirmed.length} confirmed pins`}
            </span>
          </div>
          <p className="page__note">
            {canManage
              ? mintReady
                ? "Confirmed pins that are not on any patrol route yet. Add one to an existing route on the same site without re-creating it, and mint or replace its QR and NFC tag material right here; route kits stay on Patrols."
                : "Confirmed pins that are not on any patrol route yet. Add one to an existing route on the same site without re-creating it."
              : "Confirmed pins that are not on any patrol route yet."}
          </p>
          <ul className="ckedit__rows">
            {unroutedConfirmed.map((pin) => (
              <li key={pin.checkpointId} className="ckedit__row ckedit__row--unrouted">
                <span className="ckedit__name">
                  {pin.checkpointName}
                  {pin.placementSource === "field_setup_walk" ? (
                    <>
                      {" "}
                      <span className="schedule__chip schedule__chip--credential">Field placed</span>
                    </>
                  ) : pin.placementSource === "field_guard_proposal" ? (
                    <>
                      {" "}
                      <span className="schedule__chip schedule__chip--credential">Guard proposed</span>
                    </>
                  ) : null}
                </span>
                <span className="ckedit__context">
                  {siteNameById.get(pin.siteId) ?? "Unknown site"}
                </span>
                <span className="ckedit__coords">
                  {pin.expectedLat !== null && pin.expectedLng !== null
                    ? `${pin.expectedLat.toFixed(4)}, ${pin.expectedLng.toFixed(4)}`
                    : "no coordinates"}
                </span>
                <span className="ckedit__context">{placementProvenanceLabel(pin)}</span>
                {mintReady ? <MaterialStatusBadge counts={material.get(pin.checkpointId)} /> : null}
                {canManage || mintReady ? (
                  <span className="ckedit__actions">
                    {canManage ? (
                      <button
                        type="button"
                        className="minter__confirm-go"
                        aria-label={`Add to route - ${pin.checkpointName}`}
                        disabled={busy}
                        onClick={() => {
                          setFailure(null);
                          setSuccess(null);
                          setDialog({ kind: "attach", pin });
                        }}
                      >
                        Add to route...
                      </button>
                    ) : null}
                    {canManage ? retirePinButton(pin) : null}
                    {mintReady && mintSession !== null ? (
                      <UnroutedMintActions
                        pin={pin}
                        siteName={siteNameById.get(pin.siteId) ?? null}
                        session={mintSession}
                        counts={material.get(pin.checkpointId)}
                        busy={busy}
                      />
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {groups.length === 0 ? (
        <p className="surface__state" role="status">
          No active routes yet - create a route above, then add its checkpoints here.
        </p>
      ) : (
        groups.map((group, groupIndex) => (
          <section
            key={group.route.routeId}
            className={`ckedit__group${activeRouteId === group.route.routeId ? " ckedit__group--editing" : ""}`}
            role="region"
            aria-label={`${group.route.name} checkpoints`}
          >
            <div className="ckedit__head">
              <span className="ckedit__route">{group.route.name}</span>
              <span className="ckedit__site">{group.rows[0]?.siteName ?? ""}</span>
              {canManage ? (
                <button
                  type="button"
                  className="minter__confirm-go ckedit__add"
                  onClick={() => {
                    setFailure(null);
                    setSuccess(null);
                    // Field-first default: when confirmed pins are already waiting on this
                    // site, attaching one is the likelier intent than typing a duplicate.
                    setAddMode(attachPinOptions(group.route).length > 0 ? "existing" : "new");
                    setDialog({
                      kind: "create",
                      route: group.route,
                      maxOrder: nextSortOrder(rows, group.route.routeId) - 1,
                    });
                  }}
                >
                  Add checkpoint
                </button>
              ) : null}
            </div>
            {group.rows.length === 0 ? (
              <p className="surface__state" role="status">
                No checkpoints on this route yet.
              </p>
            ) : (
              <ul className="ckedit__rows">
                <li className="ckedit__row ckedit__row--checkpoint ckedit__row--head" aria-hidden="true">
                  <span>#</span>
                  <span>Checkpoint</span>
                  <span>Scan</span>
                  <span>Policy</span>
                  <span>Site / zone</span>
                  <span>Coordinates</span>
                  <span className="ckedit__actions">Actions</span>
                </li>
                {group.rows.map((row, index) => (
                  <li
                    key={row.routeCheckpointId}
                    className={`ckedit__row ckedit__row--checkpoint${activeCheckpointId === row.routeCheckpointId ? " ckedit__row--editing" : ""}`}
                  >
                    <span className="ckedit__order">#{row.sortOrder}</span>
                    <span className="ckedit__name">
                      {row.checkpointName}
                      {/* PRD-001I: field-walk provenance is visible at a glance; console rows stay unbadged. */}
                      {row.placementSource === "field_setup_walk" ? (
                        <>
                          {" "}
                          <span
                            className="schedule__chip schedule__chip--credential"
                            title={placementProvenanceLabel(row)}
                          >
                            Field placed
                          </span>
                        </>
                      ) : row.placementSource === "field_guard_proposal" ? (
                        <>
                          {" "}
                          <span
                            className="schedule__chip schedule__chip--credential"
                            title={placementProvenanceLabel(row)}
                          >
                            Guard proposed
                          </span>
                        </>
                      ) : null}
                      {row.placementState === "provisional" ? (
                        <>
                          {" "}
                          <span className="schedule__chip schedule__chip--partial">Draft</span>
                        </>
                      ) : null}
                      {/* PRD-001 checkpoint Setup first-class, lock 7: a retired stop stays
                          listed on its routes so old reports still read correctly. It has to say
                          so, or the row reads as a live stop patrol is still expected to visit. */}
                      {row.checkpointActive ? null : (
                        <>
                          {" "}
                          <span className="schedule__chip">Retired</span>
                        </>
                      )}
                    </span>
                    <span className="schedule__chip schedule__chip--credential">
                      {formatCheckpointMethod(row.preferredMethod)}
                    </span>
                    <span className="ckedit__badge-slot">
                      {row.isRequired ? (
                        <span className="schedule__chip schedule__chip--partial">Required</span>
                      ) : (
                        <span className="ckedit__badge-placeholder">Optional</span>
                      )}
                    </span>
                    <span className="ckedit__context">
                      {/* AC-001Y.9: parent site is always known; the L1 zone is optional. */}
                      {row.siteName ?? "Unknown site"}
                      {row.siteAssetName !== null ? ` / ${row.siteAssetName}` : " / no zone"}
                    </span>
                    <span className="ckedit__coords" title={checkpointLocationLabel(row)}>
                      {/* AD5C-1: this cell used to print a lowercase "no coordinates" literal
                          and then the label below it, which says "No coordinates" on the same
                          null-coords condition - the row read "no coordinatesNo coordinates".
                          The label is the one that has to stay, because it is the word the
                          phone uses; the numbers only appear when there are numbers, and take
                          the same " · " separator the unrouted picker already uses. */}
                      {row.expectedLat !== null && row.expectedLng !== null
                        ? `${row.expectedLat.toFixed(4)}, ${row.expectedLng.toFixed(4)} · `
                        : null}
                      {/* F8: the desk and the field must use the same words for the same fact.
                          Coordinates set in the office are marked not confirmed until somebody
                          stands at the stop, because that is the flag the field walks from. */}
                      <span className="ckedit__context">{checkpointLocationLabel(row)}</span>
                    </span>
                    {canManage ||
                    (mintReady &&
                      mintSession !== null &&
                      checkpointUsesPhysicalTags(row.preferredMethod)) ? (
                      <span className="ckedit__actions">
                        {/* Badge + mint stay inside this single grid cell so they never land
                            in the #/order column and get clipped under the shell nav. */}
                        {mintReady &&
                        mintSession !== null &&
                        checkpointUsesPhysicalTags(row.preferredMethod) ? (
                          <>
                            <MaterialStatusBadge counts={material.get(row.checkpointId)} />
                            <TagMintActions
                              checkpointId={row.checkpointId}
                              checkpointName={row.checkpointName}
                              preferredMethod={row.preferredMethod}
                              routeLabel={[group.route.name, row.siteName]
                                .filter(Boolean)
                                .join(" · ")}
                              session={mintSession}
                              counts={material.get(row.checkpointId)}
                              busy={busy}
                            />
                          </>
                        ) : null}
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              className="builder__navbtn"
                              aria-label={`Move up - ${row.checkpointName}`}
                              disabled={index === 0 || busy}
                              onClick={() => void moveRow(group, index, -1)}
                            >
                              Move up
                            </button>
                            <button
                              type="button"
                              className="builder__navbtn"
                              aria-label={`Move down - ${row.checkpointName}`}
                              disabled={index === group.rows.length - 1 || busy}
                              onClick={() => void moveRow(group, index, 1)}
                            >
                              Move down
                            </button>
                            <button
                              type="button"
                              className="builder__navbtn"
                              onClick={() => {
                                setFailure(null);
                                setDialog({ kind: "edit", row });
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="builder__cancel"
                              onClick={() => {
                                setFailure(null);
                                setDialog({ kind: "remove", row });
                              }}
                            >
                              Remove
                            </button>
                            {/* F8: the console says Retire and Restore, exactly as the phone
                                does. Neither surface offers delete, because there is none. */}
                            {row.checkpointActive ? (
                              <button
                                type="button"
                                className="builder__cancel"
                                aria-label={`Retire - ${row.checkpointName}`}
                                onClick={() => {
                                  setFailure(null);
                                  setRemoveFromRoutes(false);
                                  setDialog({ kind: "retire", row });
                                }}
                              >
                                Retire
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="builder__navbtn"
                                aria-label={`Restore - ${row.checkpointName}`}
                                onClick={() => {
                                  setFailure(null);
                                  setDialog({ kind: "restore", row });
                                }}
                              >
                                Restore...
                              </button>
                            )}
                          </>
                        ) : null}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {groupIndex === groups.length - 1 ? null : null}
          </section>
        ))
      )}

      {canManage && dialog !== null && (dialog.kind === "create" || dialog.kind === "edit") ? (
        <GlassDrawer
          open
          busy={busy}
          dirty={formDirty}
          size="xl"
          ariaLabel={
            dialog.kind === "create"
              ? `Add checkpoint - ${dialog.route.name}`
              : `Edit checkpoint - ${dialog.row.checkpointName}`
          }
          onClose={clearDrawer}
        >
          {dialog.kind === "create" ? (
            <AddCheckpointModeChoice
              mode={addMode}
              routeName={dialog.route.name}
              existingCount={attachPinOptions(dialog.route).length}
              busy={busy}
              onModeChange={(next) => {
                if (busy) return;
                if (next === addMode) return;
                // Switching away from a half-typed new checkpoint throws that text away, so
                // it gets the same discard confirm as closing the drawer would.
                if (!confirmDiscardIfDirty(formDirty)) return;
                setFailure(null);
                setAddMode(next);
              }}
            />
          ) : null}
          {dialog.kind === "create" && addMode === "existing" ? (
            <AttachExistingPinForm
              key={`attach-existing-${dialog.route.routeId}`}
              route={dialog.route}
              pins={attachPinOptions(dialog.route)}
              siteNameById={siteNameById}
              busy={busy || refreshing}
              failure={failure}
              onClose={requestDrawerClose}
              onAttach={(pin, isRequired) => attachPinToRoute(pin, dialog.route, isRequired)}
            />
          ) : (
          <CheckpointForm
            key={dialog.kind === "edit" ? dialog.row.routeCheckpointId : `create-${dialog.route.routeId}`}
            dialog={dialog}
            busy={busy}
            failure={failure}
            mapTilerKey={mapTilerKey}
            site={formSite}
            siteAssets={formSiteAssets}
            occupiedCoords={formOccupiedCoords}
            onDirtyChange={setFormDirty}
            onClose={requestDrawerClose}
            onSubmit={async (values) => {
              if (dialog.kind === "create") {
                await run(async () => {
                  const created = await createCheckpoint(client, accountId, dialog.route.siteId, {
                    name: values.name,
                    locationNotes: values.locationNotes,
                    expectedLat: values.expectedLat,
                    expectedLng: values.expectedLng,
                    verificationStatus: "active",
                    statusReason: null,
                    preferredMethod: values.preferredMethod,
                    fallbackMethods: [],
                    requiredEvidence: [],
                    geofenceRadiusMeters: values.geofenceRadiusMeters,
                    geofenceMinAccuracyMeters: values.geofenceMinAccuracyMeters,
                    geofenceMinDwellSeconds: values.geofenceMinDwellSeconds,
                    siteAssetId: values.siteAssetId,
                  });
                  if (!created.ok) return created;
                  if (created.checkpointId === null) {
                    return { ok: false as const, reason: SAFE_CHECKPOINT_WRITE_FAILURE };
                  }
                  return addCheckpointToRoute(client, accountId, dialog.route.routeId, created.checkpointId, {
                    sortOrder: dialog.maxOrder + 1,
                    isRequired: values.isRequired,
                    expectedOffsetSeconds: null,
                    windowStartsSeconds: null,
                    windowEndsSeconds: null,
                  });
                });
              } else {
                // Checkpoint body (method/geofence/coords) and route membership (required flag)
                // live on different RPCs. Full-field update_checkpoint cannot set is_required;
                // that is owned by route_checkpoints via update_route_checkpoint.
                await run(async () => {
                  const body = await updateCheckpoint(
                    client,
                    accountId,
                    dialog.row.checkpointId,
                    checkpointDraftFromRow(dialog.row, {
                      name: values.name,
                      locationNotes: values.locationNotes,
                      expectedLat: values.expectedLat,
                      expectedLng: values.expectedLng,
                      preferredMethod: values.preferredMethod,
                      geofenceRadiusMeters: values.geofenceRadiusMeters,
                      geofenceMinAccuracyMeters: values.geofenceMinAccuracyMeters,
                      geofenceMinDwellSeconds: values.geofenceMinDwellSeconds,
                      siteAssetId: values.siteAssetId,
                    }),
                  );
                  if (!body.ok) return body;
                  if (dialog.row.isRequired === values.isRequired) return body;
                  return updateRouteCheckpoint(client, accountId, dialog.row.routeCheckpointId, {
                    sortOrder: dialog.row.sortOrder,
                    isRequired: values.isRequired,
                    expectedOffsetSeconds: dialog.row.expectedOffsetSeconds,
                    windowStartsSeconds: dialog.row.windowStartsSeconds,
                    windowEndsSeconds: dialog.row.windowEndsSeconds,
                  });
                });
              }
            }}
          />
          )}
        </GlassDrawer>
      ) : null}

      {canManage && dialog !== null && dialog.kind === "attach" ? (
        <GlassModal
          open
          busy={busy}
          size="lg"
          ariaLabel={`Add to route - ${dialog.pin.checkpointName}`}
          onClose={() => {
            if (busy) return;
            setFailure(null);
            setDialog(null);
          }}
        >
          <AttachPinToRouteForm
            key={dialog.pin.checkpointId}
            pin={dialog.pin}
            routes={attachRouteOptions(dialog.pin)}
            siteName={siteNameById.get(dialog.pin.siteId) ?? null}
            busy={busy || refreshing}
            failure={failure}
            onClose={() => {
              setFailure(null);
              setDialog(null);
            }}
            onAttach={(route, isRequired) => attachPinToRoute(dialog.pin, route, isRequired)}
          />
        </GlassModal>
      ) : null}

      {canManage && dialog !== null && dialog.kind === "reject-proposal" ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={`Reject guard proposal - ${dialog.proposal.proposedName}`}
          onClose={() => {
            if (busy) return;
            setFailure(null);
            setDialog(null);
          }}
        >
          <CheckpointRequestRejectForm
            key={dialog.proposal.requestId}
            proposal={dialog.proposal}
            busy={busy}
            failure={failure}
            onClose={() => {
              setFailure(null);
              setDialog(null);
            }}
            onReject={(reason) =>
              run(
                () =>
                  rejectCheckpointRequest(
                    client,
                    accountId,
                    dialog.proposal.requestId,
                    reason,
                  ),
                {
                  successMessage: "Guard proposal rejected.",
                  fallbackFailure: SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE,
                },
              )
            }
          />
        </GlassModal>
      ) : null}

      {canManage && dialog !== null && dialog.kind === "convert-proposal" ? (
        <GlassModal
          open
          busy={busy}
          size="lg"
          ariaLabel={`Convert guard proposal - ${dialog.proposal.proposedName}`}
          onClose={() => {
            if (busy) return;
            setFailure(null);
            setDialog(null);
          }}
        >
          <CheckpointRequestConvertForm
            key={dialog.proposal.requestId}
            proposal={dialog.proposal}
            busy={busy}
            failure={failure}
            onClose={() => {
              setFailure(null);
              setDialog(null);
            }}
            onConvert={(conversion) =>
              run(
                () =>
                  convertCheckpointRequest(
                    client,
                    accountId,
                    dialog.proposal.requestId,
                    conversion,
                  ),
                {
                  successMessage: "Provisional pin created. Promote when ready.",
                  fallbackFailure: SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE,
                },
              )
            }
          />
        </GlassModal>
      ) : null}

      {canManage && dialog !== null && dialog.kind === "promote" ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={`Promote placement - ${dialog.draft.checkpointName}`}
          onClose={() => {
            if (!busy) setDialog(null);
          }}
        >
          <div className="minter__confirm" aria-label={`Promote placement - ${dialog.draft.checkpointName}`}>
            <h3 className="minter__confirm-title">Promote placement - {dialog.draft.checkpointName}</h3>
            <p className="minter__confirm-warning">
              Promote confirms this provisional placement. Coordinates and field provenance stay
              exactly as saved when the draft was created. The checkpoint does not join a patrol
              route yet; it moves to the unrouted list here in Setup, where its QR tag can be minted
              before route attach.
            </p>
            <p className="minter__confirm-note">{placementProvenanceLabel(dialog.draft)}</p>
            {failure !== null ? (
              <p className="surface__state surface__state--error" role="alert">{failure}</p>
            ) : null}
            <div className="minter__confirm-actions">
              <button type="button" className="builder__navbtn" disabled={busy} onClick={() => setDialog(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="minter__confirm-go"
                disabled={busy}
                onClick={() =>
                  void run(() => promoteCheckpointPlacement(client, accountId, dialog.draft.checkpointId))
                }
              >
                Confirm promote
              </button>
            </div>
          </div>
        </GlassModal>
      ) : null}

      {canManage && dialog !== null && (dialog.kind === "remove" || dialog.kind === "retire") ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={
            dialog.kind === "remove"
              ? `Remove from route - ${dialog.row.checkpointName}`
              : `Retire checkpoint - ${dialog.row.checkpointName}`
          }
          onClose={() => {
            if (!busy) {
              setDialog(null);
              setRemoveFromRoutes(false);
            }
          }}
        >
          {dialog.kind === "remove" ? (
            <div className="minter__confirm" aria-label={`Remove from route - ${dialog.row.checkpointName}`}>
              <h3 className="minter__confirm-title">Remove from route - {dialog.row.checkpointName}</h3>
              <p className="minter__confirm-warning">
                The checkpoint leaves this route's patrol order. The checkpoint itself and its scan
                history stay intact - it can be re-added later.
              </p>
              {failure !== null ? (
                <p className="surface__state surface__state--error" role="alert">{failure}</p>
              ) : null}
              <div className="minter__confirm-actions">
                <button type="button" className="builder__navbtn" disabled={busy} onClick={() => setDialog(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="minter__confirm-go"
                  disabled={busy}
                  onClick={() => void run(() => removeCheckpointFromRoute(client, accountId, dialog.row.routeCheckpointId))}
                >
                  Confirm remove
                </button>
              </div>
            </div>
          ) : (
            <RetireCheckpointConfirm
              busy={busy}
              failure={failure}
              onCancel={() => {
                setDialog(null);
                setRemoveFromRoutes(false);
              }}
              onConfirm={() =>
                void run(
                  async () => {
                    const outcome = await archiveCheckpoint(
                      client,
                      accountId,
                      dialog.row.checkpointId,
                      removeFromRoutes,
                    );
                    // A retire that landed while the route cleanup did not is a partial result,
                    // and the operator has to be told which half happened.
                    if (outcome.ok && removeFromRoutes && !outcome.removedFromRoutes) {
                      setRemoveFromRoutes(false);
                      return {
                        ok: true,
                        partial:
                          "Retired. Removing it from its routes is not available on this workspace yet, so it is still listed on them.",
                      };
                    }
                    setRemoveFromRoutes(false);
                    return outcome;
                  },
                  { successMessage: `${dialog.row.checkpointName} is retired. Its history is kept and the name is free again.` },
                )
              }
              onToggleRemoveFromRoutes={() => setRemoveFromRoutes((value) => !value)}
              removeFromRoutes={removeFromRoutes}
              row={dialog.row}
              routes={routesForCheckpoint(rows, dialog.row.checkpointId)}
              routeImpactUnknown={routeImpactUnknown}
            />
          )}
        </GlassModal>
      ) : null}

      {/* 2026-08-20 field pin lifecycle lock, slice 1. A bad walk draft or a promoted pin that
          was never wanted lands in one of the two unrouted queues, where Promote and Mint were
          the only doors. Retire is the SAME RPC the routed row already uses; a separate dialog
          kind keeps the shipped routed confirm's rendered output untouched. An unrouted pin is
          on no route by contract, so routesForCheckpoint comes back empty and the confirm says
          so instead of offering an opt in that would do nothing.
          routesForCheckpoint is NOT a safety net for a routed pin that leaked into these queues,
          and an earlier version of this comment wrongly claimed it was: it reads the SAME capped
          rows the queues subtracted, so a pin that leaked in because its route links were
          truncated away finds nothing there either. routeImpactUnknown is the real guard, and it
          makes the confirm stop asserting rather than assert something false. */}
      {canManage && dialog !== null && dialog.kind === "retire-pin" ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={`Retire checkpoint - ${dialog.pin.checkpointName}`}
          onClose={() => {
            if (!busy) {
              setDialog(null);
              setRemoveFromRoutes(false);
            }
          }}
        >
          <RetireCheckpointConfirm
            busy={busy}
            failure={failure}
            onCancel={() => {
              setDialog(null);
              setRemoveFromRoutes(false);
            }}
            onConfirm={() =>
              void run(
                async () => {
                  const outcome = await archiveCheckpoint(
                    client,
                    accountId,
                    dialog.pin.checkpointId,
                    removeFromRoutes,
                  );
                  if (outcome.ok && removeFromRoutes && !outcome.removedFromRoutes) {
                    setRemoveFromRoutes(false);
                    return {
                      ok: true,
                      partial:
                        "Retired. Removing it from its routes is not available on this workspace yet, so it is still listed on them.",
                    };
                  }
                  setRemoveFromRoutes(false);
                  return outcome;
                },
                {
                  // Lock 6: a retired unrouted pin leaves this surface entirely, because the
                  // placement reader is active-only. Saying where it went is the difference
                  // between a retire and a disappearance.
                  //
                  // Lock 7 applies to the receipt as well as the confirm. CAD keeps an inactive
                  // checkpoint that is still route-attached, so on a truncated read this pin may
                  // not leave the map, and a receipt that says it did would put the false claim
                  // back one screen later.
                  successMessage: routeImpactUnknown
                    ? `${dialog.pin.checkpointName} is retired. It leaves this list, its history is kept, and the name is free again. If it turns out to be on a patrol route it stays there marked retired, and patrol skips it. Setup on the phone lists it under Retired if it needs to come back.`
                    : `${dialog.pin.checkpointName} is retired. It leaves this list and the map, its history is kept, and the name is free again. Setup on the phone lists it under Retired if it needs to come back.`,
                },
              )
            }
            onToggleRemoveFromRoutes={() => setRemoveFromRoutes((value) => !value)}
            removeFromRoutes={removeFromRoutes}
            row={dialog.pin}
            routes={routesForCheckpoint(rows, dialog.pin.checkpointId)}
            routeImpactUnknown={routeImpactUnknown}
            activeMaterialCount={material.get(dialog.pin.checkpointId)?.activeCheckpointMaterial}
          />
        </GlassModal>
      ) : null}

      {/* F8, lock 9: restore offers two honest outcomes and never forces one. Live requires
          coordinates; a stop that has none comes back in the book so a walk can finish it. */}
      {canManage && dialog !== null && dialog.kind === "restore" ? (
        <GlassModal
          open
          busy={busy}
          ariaLabel={`Restore checkpoint - ${dialog.row.checkpointName}`}
          onClose={() => {
            if (!busy) setDialog(null);
          }}
        >
          <RestoreCheckpointConfirm
            busy={busy}
            failure={failure}
            onCancel={() => setDialog(null)}
            onRestore={(outcome) =>
              void run(
                () =>
                  restoreCheckpoint(
                    client,
                    accountId,
                    dialog.row.siteId,
                    dialog.row.checkpointId,
                    outcome,
                  ),
                {
                  successMessage:
                    outcome === "live"
                      ? `${dialog.row.checkpointName} is back and ready for patrol.`
                      : `${dialog.row.checkpointName} is back in the book, not patrol-ready yet.`,
                },
              )
            }
            row={dialog.row}
          />
        </GlassModal>
      ) : null}
    </div>
  );
}

/**
 * F7 / F8. Retire, not delete. The word delete appears here only in the sentence that says there
 * is none. Route impact is named with the actual route names, and removing the stop from those
 * routes is an opt in that starts OFF: a route that suddenly has one fewer stop rewrites the past,
 * making last week's completed walks look like they covered a route they did not.
 */
function RetireCheckpointConfirm({
  busy,
  failure,
  onCancel,
  onConfirm,
  onToggleRemoveFromRoutes,
  removeFromRoutes,
  row,
  routes,
  routeImpactUnknown = false,
  activeMaterialCount,
}: {
  busy: boolean;
  failure: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  onToggleRemoveFromRoutes: () => void;
  removeFromRoutes: boolean;
  /**
   * Only the name is read, so a routed setup row and an unrouted placement row both fit. Keeping
   * this structural rather than naming CheckpointSetupRow is what lets the unrouted door reuse
   * the shipped confirm instead of growing a second one that could drift from it.
   */
  row: { checkpointName: string };
  routes: Array<{ routeId: string; routeName: string }>;
  /**
   * True when the caller's route knowledge is capped and therefore incomplete. It never removes
   * a route the caller DOES know about; it only stops this confirm from turning silence into
   * the claim that there are no others. Left false, nothing extra renders, so every workspace
   * under the cap sees exactly the shipped wording.
   */
  routeImpactUnknown?: boolean | undefined;
  /**
   * Active QR plus NFC material on this pin, when Setup knows it. Undefined means unknown (no
   * tokens.mint, or the counts view is not on this remote), and unknown must stay silent: a
   * confident "no tags" on a pin that really carries material is the exact false-zero the
   * material-status view was built to avoid.
   */
  activeMaterialCount?: number | undefined;
}) {
  return (
    <div className="minter__confirm" aria-label={`Retire checkpoint - ${row.checkpointName}`}>
      <h3 className="minter__confirm-title">Retire checkpoint - {row.checkpointName}</h3>
      <p className="minter__confirm-warning">
        Retiring turns a stop off everywhere without losing anything it recorded. It stops accepting
        scans and mounted tags stop validating. Its full history is kept, the name becomes free for
        a new stop at this site, and you can restore it later.
      </p>
      {routes.length > 0 ? (
        <div className="minter__confirm-note">
          <strong>
            {routes.length === 1 ? "It is on 1 route right now" : `It is on ${routes.length} routes right now`}
          </strong>
          <ul>
            {routes.map((route) => (
              <li key={route.routeId}>{route.routeName}</li>
            ))}
          </ul>
          <p>
            By default it stays listed on {routes.length === 1 ? "this route" : "these routes"} as a
            retired stop, so the route history still makes sense. Patrol will skip it.
          </p>
          {routeImpactUnknown ? (
            <p>
              This workspace has more patrol stops than this page loads at once, so the list above
              may not be every route it is on. Removing it takes it off every route it is on, not
              only the ones named here.
            </p>
          ) : null}
          <label className="minter__confirm-optin">
            <input
              type="checkbox"
              checked={removeFromRoutes}
              disabled={busy}
              onChange={onToggleRemoveFromRoutes}
            />
            <span>
              Also remove it from {routes.length === 1 ? "that route" : `${routes.length === 2 ? "both" : `all ${routes.length}`} routes`}
              <small>
                Choose this if the stop is gone for good and the routes should be shorter from now
                on.
              </small>
            </span>
          </label>
        </div>
      ) : routeImpactUnknown ? (
        <p className="minter__confirm-note">
          This workspace has more patrol stops than this page loads at once, so its route
          membership could not be confirmed here. Retiring is safe either way: a retired stop
          stays on any route it is on, marked retired, and patrol skips it.
        </p>
      ) : (
        <p className="minter__confirm-note">It is not on any route right now.</p>
      )}
      {/* Lock 4: retire does not revoke token rows, because the physical tag is still on the
          wall and a restore should bring it back working. Scans are refused structurally, at
          the checkpoint join in intake. That is safe, but only the copy can make it honest, so
          the count is named when it is known. */}
      {activeMaterialCount !== undefined && activeMaterialCount > 0 ? (
        <p className="minter__confirm-note">
          {activeMaterialCount === 1
            ? "It has 1 active tag. That tag stops validating while the stop is retired, and works again if you restore it."
            : `It has ${activeMaterialCount} active tags. They stop validating while the stop is retired, and work again if you restore it.`}
        </p>
      ) : null}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={onConfirm}>
          Retire checkpoint
        </button>
      </div>
      <p className="minter__confirm-note">Nothing is deleted. There is no delete for checkpoints.</p>
    </div>
  );
}

/**
 * F8, lock 9. Two outcomes, always both offered. Restoring never edits routes: the stop comes back
 * and the operator decides where it belongs.
 */
function RestoreCheckpointConfirm({
  busy,
  failure,
  onCancel,
  onRestore,
  row,
}: {
  busy: boolean;
  failure: string | null;
  onCancel: () => void;
  onRestore: (outcome: CheckpointRestoreOutcome) => void;
  row: CheckpointSetupRow;
}) {
  const hasCoordinates = row.expectedLat !== null && row.expectedLng !== null;
  return (
    <div className="minter__confirm" aria-label={`Restore checkpoint - ${row.checkpointName}`}>
      <h3 className="minter__confirm-title">Bring {row.checkpointName} back</h3>
      <p className="minter__confirm-warning">
        It kept its coordinates and its history while it was retired. Choose how it comes back.
      </p>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="builder__navbtn"
          disabled={busy}
          onClick={() => onRestore("book_not_patrol_ready")}
        >
          In the book, not patrol-ready yet
        </button>
        <button
          type="button"
          className="minter__confirm-go"
          disabled={busy || !hasCoordinates}
          title={
            hasCoordinates
              ? undefined
              : "This stop has no coordinates, so somebody has to confirm its location on site before patrol can use it."
          }
          onClick={() => onRestore("live")}
        >
          Live, ready for patrol
        </button>
      </div>
      <p className="minter__confirm-note">
        {hasCoordinates
          ? "Restoring never touches routes. Add it back where you want it."
          : "This stop has no coordinates. Bring it back in the book, then confirm its location on site from the phone."}
      </p>
    </div>
  );
}

/**
 * Q2a Existing-vs-New for a route's Add. The choice sits above the body so the operator sees
 * both doors before typing anything; picking Existing is what stops a field-placed pin from
 * being re-created by hand as a second checkpoint at the same spot.
 */
function AddCheckpointModeChoice({
  mode,
  routeName,
  existingCount,
  busy,
  onModeChange,
}: {
  mode: "existing" | "new";
  routeName: string;
  existingCount: number;
  busy: boolean;
  onModeChange: (mode: "existing" | "new") => void;
}) {
  return (
    <fieldset className="tpl__fieldset ckrequest__choices">
      <legend className="tpl__legend">Add to {routeName}</legend>
      <label className="ckrequest__choice">
        <input
          type="radio"
          name="checkpoint-add-mode"
          value="existing"
          checked={mode === "existing"}
          disabled={busy || existingCount === 0}
          onChange={() => onModeChange("existing")}
        />
        <span>
          <strong>Existing pin</strong>
          <small>
            {existingCount === 0
              ? "No confirmed unrouted pins on this site. Promote a field walk draft first, or add a brand new stop instead."
              : existingCount === 1
                ? "1 confirmed pin on this site is not on a route yet. Attaching keeps its field coordinates and any tags already minted for it."
                : `${existingCount} confirmed pins on this site are not on a route yet. Attaching keeps field coordinates and any tags already minted.`}
          </small>
        </span>
      </label>
      <label className="ckrequest__choice">
        <input
          type="radio"
          name="checkpoint-add-mode"
          value="new"
          checked={mode === "new"}
          disabled={busy}
          onChange={() => onModeChange("new")}
        />
        <span>
          <strong>New checkpoint</strong>
          <small>Create a checkpoint from an address or coordinates and put it on this route.</small>
        </span>
      </label>
    </fieldset>
  );
}

/** Route side of A1: pick one confirmed unrouted pin on this route's site. */
function AttachExistingPinForm({
  route,
  pins,
  siteNameById,
  busy,
  failure,
  onClose,
  onAttach,
}: {
  route: EditableRoute;
  pins: readonly PlacementCheckpointRow[];
  siteNameById: ReadonlyMap<string, string>;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onAttach: (pin: PlacementCheckpointRow, isRequired: boolean) => Promise<boolean>;
}) {
  const [checkpointId, setCheckpointId] = useState(pins[0]?.checkpointId ?? "");
  const [isRequired, setIsRequired] = useState(true);
  // A refresh can land while this form is open (a failed attach forces one), so the stored id
  // may no longer be a candidate - someone else routed that pin. Falling back to the first
  // remaining pin keeps the picker usable instead of silently disabling submit on a selection
  // the operator can no longer see.
  const selected = pins.find((pin) => pin.checkpointId === checkpointId) ?? pins[0] ?? null;
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={`Add existing pin - ${route.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (selected === null) return;
        void onAttach(selected, isRequired);
      }}
    >
      <h3 className="minter__confirm-title">Add an existing pin to {route.name}</h3>
      {pins.length === 0 ? (
        <>
          <p className="minter__confirm-note">
            {siteNameById.get(route.siteId) ?? "This site"} has no confirmed pins waiting for a
            route. Field walk drafts have to be promoted before they can join one.
          </p>
          <GlassDrawerFooter>
            <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
              Close
            </button>
          </GlassDrawerFooter>
        </>
      ) : (
        <>
          <p className="minter__confirm-note">
            The pin keeps its name, coordinates, field provenance, and any tag material already
            minted for it. It goes on the end of the patrol order and can be moved from the route
            list.
          </p>
          <label className="tpl__field">
            Unrouted pin
            <select
              value={selected?.checkpointId ?? ""}
              disabled={busy}
              onChange={(event) => setCheckpointId(event.target.value)}
            >
              {pins.map((pin) => (
                <option key={pin.checkpointId} value={pin.checkpointId}>
                  {pin.checkpointName}
                </option>
              ))}
            </select>
          </label>
          {selected !== null ? (
            <p className="minter__confirm-note">
              {placementProvenanceLabel(selected)}
              {selected.expectedLat !== null && selected.expectedLng !== null
                ? ` · ${selected.expectedLat.toFixed(4)}, ${selected.expectedLng.toFixed(4)}`
                : " · no coordinates"}
            </p>
          ) : null}
          <RouteStopRequirementField
            isRequired={isRequired}
            disabled={busy}
            onChange={setIsRequired}
          />
          {failure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {failure}
            </p>
          ) : null}
          <GlassDrawerFooter>
            <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              form={formId}
              className="minter__confirm-go"
              disabled={busy || selected === null}
            >
              Add to route
            </button>
          </GlassDrawerFooter>
        </>
      )}
    </form>
  );
}

/** Pin side of A1: pick which active route on this pin's own site it should join. */
function AttachPinToRouteForm({
  pin,
  routes,
  siteName,
  busy,
  failure,
  onClose,
  onAttach,
}: {
  pin: PlacementCheckpointRow;
  routes: readonly EditableRoute[];
  siteName: string | null;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onAttach: (route: EditableRoute, isRequired: boolean) => Promise<boolean>;
}) {
  const [routeId, setRouteId] = useState(routes[0]?.routeId ?? "");
  const [isRequired, setIsRequired] = useState(true);
  // Same staleness guard as the pin picker: a route can be archived out from under an open
  // dialog, and a dead selection must not silently disable the only button here.
  const selected = routes.find((route) => route.routeId === routeId) ?? routes[0] ?? null;

  return (
    <form
      className="minter__confirm tpl__form"
      aria-label={`Add to route - ${pin.checkpointName}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (selected === null) return;
        void onAttach(selected, isRequired);
      }}
    >
      <h3 className="minter__confirm-title">Add {pin.checkpointName} to a route</h3>
      {routes.length === 0 ? (
        <>
          <p className="minter__confirm-note">
            {siteName ?? "This pin's site"} has no active patrol route yet. Create one in Routes
            above, then come back and add this pin to it.
          </p>
          <div className="minter__confirm-actions">
            <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
              Close
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="minter__confirm-note">
            Only active routes on {siteName ?? "this pin's site"} can take it. The pin keeps its
            coordinates, provenance, and any tag material already minted, and goes on the end of
            the patrol order.
          </p>
          <p className="minter__confirm-note">{placementProvenanceLabel(pin)}</p>
          <label className="tpl__field">
            Route
            <select
              value={selected?.routeId ?? ""}
              disabled={busy}
              onChange={(event) => setRouteId(event.target.value)}
            >
              {routes.map((route) => (
                <option key={route.routeId} value={route.routeId}>
                  {route.name}
                </option>
              ))}
            </select>
          </label>
          <RouteStopRequirementField
            isRequired={isRequired}
            disabled={busy}
            onChange={setIsRequired}
          />
          {failure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {failure}
            </p>
          ) : null}
          <div className="minter__confirm-actions">
            <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="minter__confirm-go"
              disabled={busy || selected === null}
            >
              Add to route
            </button>
          </div>
        </>
      )}
    </form>
  );
}

/**
 * Route-stop required flag. DB defaults is_required true when null; operators need an explicit
 * optional choice on attach and edit (the edit form was the only earlier place they could flip
 * it, and even that never wrote the field until this slice).
 */
function RouteStopRequirementField({
  isRequired,
  disabled,
  onChange,
}: {
  isRequired: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <fieldset className="tpl__fieldset">
      <legend className="tpl__legend">Patrol requirement</legend>
      <p className="minter__confirm-note">
        Required stops count toward the route completion. Optional stops still appear on the route
        for the guard but can be skipped without failing the patrol.
      </p>
      <label className="tpl__field">
        Stop type
        <select
          aria-label="Patrol stop requirement"
          value={isRequired ? "required" : "optional"}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value === "required")}
        >
          <option value="required">Required</option>
          <option value="optional">Optional</option>
        </select>
      </label>
    </fieldset>
  );
}

/**
 * A2 material state, counts and types only (Q4a). "Material state unknown" is shown verbatim
 * rather than guessed when the counts read is unavailable - that honesty is the whole point,
 * and no token body or human code appears here at any state.
 */
function MaterialStatusBadge({
  counts,
}: {
  counts: CheckpointMaterialStatusRow | undefined;
}) {
  const state = unroutedMaterialState(counts);
  if (state === "unknown") {
    return (
      <span className="schedule__chip schedule__chip--partial">Material state unknown</span>
    );
  }
  if (state === "none") {
    return <span className="ckedit__badge-placeholder">No tag material yet</span>;
  }
  return (
    <span className="schedule__chip schedule__chip--credential">
      {formatUnroutedMaterialBadge(counts!)}
    </span>
  );
}

function CheckpointRequestRejectForm({
  proposal,
  busy,
  failure,
  onClose,
  onReject,
}: {
  proposal: CheckpointRequestRow;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onReject: (reason: string | null) => Promise<boolean>;
}) {
  const [reason, setReason] = useState("");
  return (
    <form
      className="minter__confirm tpl__form"
      aria-label={`Reject guard proposal - ${proposal.proposedName}`}
      onSubmit={(event) => {
        event.preventDefault();
        void onReject(reason.trim() === "" ? null : reason);
      }}
    >
      <h3 className="minter__confirm-title">Reject guard proposal</h3>
      <p className="minter__confirm-warning">
        Reject {proposal.proposedName} at {proposal.siteName}. The guard proposal closes without
        creating a checkpoint.
      </p>
      <label className="tpl__field">
        Reason (optional)
        <textarea
          value={reason}
          maxLength={500}
          rows={4}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="minter__confirm-go" disabled={busy}>
          Confirm reject
        </button>
      </div>
    </form>
  );
}

function CheckpointRequestConvertForm({
  proposal,
  busy,
  failure,
  onClose,
  onConvert,
}: {
  proposal: CheckpointRequestRow;
  busy: boolean;
  failure: string | null;
  onClose: () => void;
  onConvert: (conversion: CheckpointRequestConversion) => Promise<boolean>;
}) {
  const [mode, setMode] = useState<CheckpointRequestConvertMode>("accept_guard_draft");
  const [managerLat, setManagerLat] = useState("");
  const [managerLng, setManagerLng] = useState("");
  const [managerAccuracy, setManagerAccuracy] = useState("");
  const [validation, setValidation] = useState<string | null>(null);

  function selectMode(nextMode: CheckpointRequestConvertMode): void {
    setMode(nextMode);
    setValidation(null);
  }

  function submitConversion(): void {
    setValidation(null);
    if (mode === "accept_guard_draft") {
      void onConvert({
        mode,
        nameOverride: proposal.proposedName,
        managerLat: null,
        managerLng: null,
        managerAccuracyMeters: null,
      });
      return;
    }

    if (
      managerLat.trim() === "" ||
      managerLng.trim() === "" ||
      managerAccuracy.trim() === ""
    ) {
      setValidation("Manager latitude, longitude, and GPS accuracy are required.");
      return;
    }
    const lat = Number(managerLat);
    const lng = Number(managerLng);
    const accuracy = Number(managerAccuracy);
    if (
      !Number.isFinite(lat) ||
      lat < -90 ||
      lat > 90 ||
      !Number.isFinite(lng) ||
      lng < -180 ||
      lng > 180
    ) {
      setValidation("Manager coordinates must be valid and in range.");
      return;
    }
    if (!Number.isFinite(accuracy) || accuracy < 0) {
      setValidation("Manager GPS accuracy must be zero or greater.");
      return;
    }
    void onConvert({
      mode,
      nameOverride: proposal.proposedName,
      managerLat: lat,
      managerLng: lng,
      managerAccuracyMeters: accuracy,
    });
  }

  return (
    <form
      className="minter__confirm tpl__form"
      aria-label={`Convert guard proposal - ${proposal.proposedName}`}
      onSubmit={(event) => {
        event.preventDefault();
        submitConversion();
      }}
    >
      <h3 className="minter__confirm-title">Convert proposal</h3>
      <p className="minter__confirm-note">
        {proposal.proposedName} at {proposal.siteName}. Guard GPS accuracy was{" "}
        {proposal.captureAccuracyMeters} m.
      </p>
      <p className="minter__confirm-warning">
        Conversion creates a provisional pin only. Promote it later from Field walk drafts.
      </p>
      <fieldset className="tpl__fieldset ckrequest__choices">
        <legend className="tpl__legend">Placement source</legend>
        <label className="ckrequest__choice">
          <input
            type="radio"
            name="checkpoint-request-convert-mode"
            value="accept_guard_draft"
            checked={mode === "accept_guard_draft"}
            onChange={() => selectMode("accept_guard_draft")}
          />
          <span>
            <strong>Accept guard location as draft</strong>
            <small>Use the guard coordinates and keep the result provisional.</small>
          </span>
        </label>
        <label className="ckrequest__choice">
          <input
            type="radio"
            name="checkpoint-request-convert-mode"
            value="re_place"
            checked={mode === "re_place"}
            onChange={() => selectMode("re_place")}
          />
          <span>
            <strong>Re-place with my coordinates</strong>
            <small>Use manager GPS under the account Setup accuracy threshold.</small>
          </span>
        </label>
      </fieldset>
      {mode === "re_place" ? (
        <fieldset className="tpl__fieldset">
          <legend className="tpl__legend">Manager placement</legend>
          <div className="tpl__field-row tpl__field-row--coords">
            <label className="tpl__field">
              Manager latitude
              <input
                type="number"
                step="any"
                min={-90}
                max={90}
                value={managerLat}
                onChange={(event) => setManagerLat(event.target.value)}
              />
            </label>
            <label className="tpl__field">
              Manager longitude
              <input
                type="number"
                step="any"
                min={-180}
                max={180}
                value={managerLng}
                onChange={(event) => setManagerLng(event.target.value)}
              />
            </label>
          </div>
          <label className="tpl__field">
            Manager GPS accuracy (m)
            <input
              type="number"
              step="any"
              min={0}
              value={managerAccuracy}
              onChange={(event) => setManagerAccuracy(event.target.value)}
            />
          </label>
        </fieldset>
      ) : null}
      {validation !== null ? (
        <p className="surface__state surface__state--error" role="alert">{validation}</p>
      ) : null}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
      <div className="minter__confirm-actions">
        <button type="button" className="builder__navbtn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="minter__confirm-go" disabled={busy}>
          Convert to provisional pin
        </button>
      </div>
    </form>
  );
}

/**
 * Tag mint cluster (unrouted pins + routed QR/NFC). Wave A2 material honesty:
 *
 *   present -> Replace material only (never a fresh mint that the server would refuse).
 *   none    -> preferred-method-aware Mint QR / Mint kit only.
 *   unknown -> both, with materialUnknown on the confirm (missing read is not known-empty).
 *
 * Session kit always wins as proof of material. Raw secrets only in the session-kit modal.
 * Patrols still owns bulk route "Mint kit"; this cluster is one checkpoint per click.
 */
function TagMintActions({
  checkpointId,
  checkpointName,
  preferredMethod,
  routeLabel,
  session,
  counts,
  busy,
}: {
  checkpointId: string;
  checkpointName: string;
  preferredMethod: string | null;
  routeLabel: string;
  session: TokenMintSessionContextValue;
  counts: CheckpointMaterialStatusRow | undefined;
  busy: boolean;
}) {
  const hasKit = session.hasSessionKit(checkpointId);
  const countState = unroutedMaterialState(counts);
  const state: UnroutedMaterialState = hasKit ? "present" : countState;
  const primaryLabel = checkpointMintPrimaryLabel(preferredMethod);
  const confirmBase = {
    checkpointId,
    checkpointName,
    routeLabel,
  };
  return (
    <>
      {hasKit ? (
        <button
          type="button"
          className="builder__navbtn"
          aria-label={`View kit for ${checkpointName}`}
          onClick={() => session.openSessionKit(checkpointId)}
        >
          View kit
        </button>
      ) : null}
      {!hasKit && state !== "present" ? (
        <button
          type="button"
          className="minter__confirm-go"
          aria-label={`${primaryLabel} - ${checkpointName}`}
          disabled={busy}
          onClick={() =>
            session.openCheckpointConfirm({
              ...confirmBase,
              hasExistingMaterial: false,
              replace: false,
              ...(state === "unknown" ? { materialUnknown: true } : {}),
            })
          }
        >
          {primaryLabel}
        </button>
      ) : null}
      {state !== "none" ? (
        <button
          type="button"
          className={state === "present" && !hasKit ? "minter__confirm-go" : "builder__navbtn"}
          aria-label={`Replace material - ${checkpointName}`}
          disabled={busy}
          onClick={() =>
            session.openCheckpointConfirm({
              ...confirmBase,
              hasExistingMaterial: state === "present",
              replace: true,
              ...(state === "unknown" ? { materialUnknown: true } : {}),
            })
          }
        >
          Replace material
        </button>
      ) : null}
    </>
  );
}

function UnroutedMintActions({
  pin,
  siteName,
  session,
  counts,
  busy,
}: {
  pin: PlacementCheckpointRow;
  siteName: string | null;
  session: TokenMintSessionContextValue;
  counts: CheckpointMaterialStatusRow | undefined;
  busy: boolean;
}) {
  const contextLabel = [
    siteName,
    pin.placementState === "provisional" ? "Draft pin, not on a route" : "Not on a route",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <TagMintActions
      checkpointId={pin.checkpointId}
      checkpointName={pin.checkpointName}
      preferredMethod={pin.preferredMethod}
      routeLabel={contextLabel}
      session={session}
      counts={counts}
      busy={busy}
    />
  );
}

interface FormValues {
  name: string;
  locationNotes: string | null;
  expectedLat: number | null;
  expectedLng: number | null;
  preferredMethod: string;
  geofenceRadiusMeters: number | null;
  geofenceMinAccuracyMeters: number | null;
  geofenceMinDwellSeconds: number | null;
  siteAssetId: string | null;
  /** Route membership only (create / edit on a route). Attach forms set this separately. */
  isRequired: boolean;
}

/**
 * Blank -> null; otherwise a finite, strictly positive number (matches
 * checkpoints_geofence_radius_positive / checkpoints_geofence_accuracy_positive). Zero is
 * rejected client-side so operators get an honest reason instead of a generic RPC failure.
 */
function parsePositiveGeofenceMeters(
  value: string,
  fieldLabel: string,
): { ok: true; value: number | null } | { ok: false; reason: string } {
  if (value.trim() === "") {
    return { ok: true, value: null };
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return {
      ok: false,
      reason: `${fieldLabel} must be blank or greater than zero meters.`,
    };
  }
  return { ok: true, value: parsed };
}

/** Blank -> null; non-negative, integer-rounded dwell seconds for the Postgres integer column. */
function parseDwellSeconds(
  value: string,
): { ok: true; value: number | null } | { ok: false; reason: string } {
  if (value.trim() === "") {
    return { ok: true, value: null };
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { ok: false, reason: "Min dwell must be blank or zero or more seconds." };
  }
  return { ok: true, value: Math.round(parsed) };
}

/** Core-field form shared by create (then attach) and edit (full round-trip upstream). */
function CheckpointForm({
  dialog,
  busy,
  failure,
  mapTilerKey,
  site,
  siteAssets,
  occupiedCoords,
  onClose,
  onSubmit,
  onDirtyChange,
}: {
  dialog: { kind: "create"; route: EditableRoute } | { kind: "edit"; row: CheckpointSetupRow };
  busy: boolean;
  failure: string | null;
  mapTilerKey?: string | undefined;
  site: ManagedSite | null;
  /** Active L1 zones on this checkpoint's site (already filtered upstream). */
  siteAssets: readonly SiteAsset[];
  occupiedCoords: readonly { lat: number; lng: number }[];
  onClose: () => void;
  onSubmit: (values: FormValues) => void | Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const editing = dialog.kind === "edit" ? dialog.row : null;
  const initialName = editing?.checkpointName ?? "";
  const initialNotes = editing?.locationNotes ?? "";
  const initialAddressQuery =
    dialog.kind === "create" && site !== null ? formatGeocodeQuery(site) : "";
  const initialLat =
    editing?.expectedLat !== null && editing !== null ? String(editing.expectedLat) : "";
  const initialLng =
    editing?.expectedLng !== null && editing !== null ? String(editing.expectedLng) : "";
  const initialMethod = editing?.preferredMethod ?? "qr";
  const initialRadius =
    editing?.geofenceRadiusMeters != null ? String(editing.geofenceRadiusMeters) : "";
  const initialAccuracy =
    editing?.geofenceMinAccuracyMeters != null ? String(editing.geofenceMinAccuracyMeters) : "";
  const initialDwell =
    editing?.geofenceMinDwellSeconds != null ? String(editing.geofenceMinDwellSeconds) : "";
  // "" is the None option. A stale link to a cleared zone already reads back as null upstream.
  const initialAssetId = editing?.siteAssetId ?? "";
  const initialRequired = editing?.isRequired ?? true;
  const [name, setName] = useState(initialName);
  const [notes, setNotes] = useState(initialNotes);
  const [addressQuery, setAddressQuery] = useState(initialAddressQuery);
  const [lat, setLat] = useState(initialLat);
  const [lng, setLng] = useState(initialLng);
  const [method, setMethod] = useState(initialMethod);
  const [radius, setRadius] = useState(initialRadius);
  const [accuracy, setAccuracy] = useState(initialAccuracy);
  const [dwell, setDwell] = useState(initialDwell);
  const [assetId, setAssetId] = useState(initialAssetId);
  const [isRequired, setIsRequired] = useState(initialRequired);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [localFailure, setLocalFailure] = useState<string | null>(null);
  const methodHint = checkpointMethodHint(method);
  // The action row renders on the drawer's panel edge, outside this form's DOM subtree,
  // so its submit button carries `form=` to stay bound to this form.
  const formId = useId();

  const dirty =
    name !== initialName ||
    notes !== initialNotes ||
    addressQuery !== initialAddressQuery ||
    lat !== initialLat ||
    lng !== initialLng ||
    method !== initialMethod ||
    radius !== initialRadius ||
    accuracy !== initialAccuracy ||
    dwell !== initialDwell ||
    assetId !== initialAssetId ||
    isRequired !== initialRequired;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (method !== "gps_geofence" || radius.trim() !== "") return;
    setRadius(String(DEFAULT_GPS_GEOFENCE_RADIUS_METERS));
  }, [method, radius]);

  async function lookupAddress(): Promise<void> {
    if (lookupBusy) return;
    setLookupBusy(true);
    setLookupMessage(null);
    const outcome = await geocodeAddressWithNudge(addressQuery, mapTilerKey, occupiedCoords);
    if (outcome.ok) {
      setLat(String(outcome.result.lat));
      setLng(String(outcome.result.lng));
      if (notes.trim() === "") {
        setNotes(outcome.result.label);
      }
      const placed = `${outcome.result.lat.toFixed(5)}, ${outcome.result.lng.toFixed(5)}`;
      setLookupMessage(
        outcome.wasNudged
          ? `Placed near the site at ${placed} (offset so pins do not stack).`
          : `Placed at ${placed}`,
      );
    } else {
      setLookupMessage(outcome.reason);
    }
    setLookupBusy(false);
  }

  return (
    <form
      id={formId}
      className="minter__confirm tpl__form"
      aria-label={dialog.kind === "create" ? `New checkpoint - ${dialog.route.name}` : `Edit checkpoint - ${editing!.checkpointName}`}
      onSubmit={(event) => {
        event.preventDefault();
        setLocalFailure(null);
        const parsedLat = lat.trim() === "" ? null : Number(lat);
        const parsedLng = lng.trim() === "" ? null : Number(lng);
        let radiusValue = radius;
        if (method === "gps_geofence" && radiusValue.trim() === "") {
          radiusValue = String(DEFAULT_GPS_GEOFENCE_RADIUS_METERS);
          setRadius(radiusValue);
        }
        const radiusOutcome = parsePositiveGeofenceMeters(radiusValue, "Geofence radius");
        if (!radiusOutcome.ok) {
          setLocalFailure(radiusOutcome.reason);
          return;
        }
        if (method === "gps_geofence" && radiusOutcome.value === null) {
          setLocalFailure("GPS geofence checkpoints need a radius greater than zero meters.");
          return;
        }
        const accuracyOutcome = parsePositiveGeofenceMeters(accuracy, "Min GPS accuracy");
        if (!accuracyOutcome.ok) {
          setLocalFailure(accuracyOutcome.reason);
          return;
        }
        const dwellOutcome = parseDwellSeconds(dwell);
        if (!dwellOutcome.ok) {
          setLocalFailure(dwellOutcome.reason);
          return;
        }
        void onSubmit({
          name: name.trim(),
          locationNotes: notes.trim() === "" ? null : notes.trim(),
          expectedLat: parsedLat !== null && Number.isFinite(parsedLat) ? parsedLat : null,
          expectedLng: parsedLng !== null && Number.isFinite(parsedLng) ? parsedLng : null,
          preferredMethod: method,
          geofenceRadiusMeters: radiusOutcome.value,
          geofenceMinAccuracyMeters: accuracyOutcome.value,
          geofenceMinDwellSeconds: dwellOutcome.value,
          siteAssetId: assetId === "" ? null : assetId,
          isRequired,
        });
      }}
    >
      <h3 className="minter__confirm-title">
        {dialog.kind === "create" ? `New checkpoint on ${dialog.route.name}` : `Edit checkpoint - ${editing!.checkpointName}`}
      </h3>
      {dialog.kind === "create" ? (
        <p className="minter__confirm-note">
          This stop is saved on the route in one step. Look up the street address to drop a pin on
          the Command Center map, or type coordinates. After saving a QR or NFC checkpoint, mint
          its one-time print/export kit from that checkpoint row in Setup; NFC tags are written
          with an external writer.
        </p>
      ) : (
        <p className="minter__confirm-note">
          Coordinates and verification method update immediately. Fallback methods and evidence
          rules stay as configured until their dedicated editor ships.
        </p>
      )}
      {editing !== null ? (
        // PRD-001I AC-001I.4.2: read-only provenance so a desk operator can tell a field-walk
        // pin from a console one before editing its coordinates. Saving never changes it.
        <p className="minter__confirm-note">{placementProvenanceLabel(editing)}</p>
      ) : null}
      <label className="tpl__field">
        Name
        <input type="text" value={name} onChange={(event) => setName(event.target.value)} required />
      </label>
      <label className="tpl__field">
        Location notes (optional)
        <input type="text" value={notes} onChange={(event) => setNotes(event.target.value)} />
      </label>
      <fieldset className="tpl__fieldset">
        <legend className="tpl__legend">Map position</legend>
        <p className="minter__confirm-note">
          Search by street address to fill latitude and longitude, then nudge the numbers if the
          pin needs to move. If the address matches the site, we offset the pin slightly so it does
          not stack on the property center. Required for GPS geofence checkpoints; optional for QR/NFC
          tags mounted nearby.
        </p>
        <label className="tpl__field">
          Street address lookup
          <input
            type="text"
            value={addressQuery}
            onChange={(event) => setAddressQuery(event.target.value)}
            placeholder="e.g. 100 Main Street, Springfield IL"
          />
        </label>
        <div className="minter__confirm-actions setup__lookup-actions">
          <button
            type="button"
            className="builder__navbtn"
            disabled={busy || lookupBusy || addressQuery.trim().length === 0}
            onClick={() => void lookupAddress()}
          >
            {lookupBusy ? "Looking up..." : "Look up address"}
          </button>
        </div>
        {lookupMessage !== null ? (
          <p
            className={
              lookupMessage.startsWith("Placed at")
                ? "minter__confirm-note"
                : "surface__state surface__state--error"
            }
            role={lookupMessage.startsWith("Placed at") ? "status" : "alert"}
          >
            {lookupMessage}
          </p>
        ) : null}
        <div className="tpl__field-row tpl__field-row--coords">
          <label className="tpl__field">
            Latitude
            <input type="number" step="any" value={lat} onChange={(event) => setLat(event.target.value)} />
          </label>
          <label className="tpl__field">
            Longitude
            <input type="number" step="any" value={lng} onChange={(event) => setLng(event.target.value)} />
          </label>
        </div>
        {/* AC-001Y.9: optional L1 zone. Only this site's active zones are offered; None clears it. */}
        <label className="tpl__field">
          Asset zone (optional)
          <select
            value={assetId}
            onChange={(event) => setAssetId(event.target.value)}
            disabled={siteAssets.length === 0}
          >
            <option value="">None</option>
            {siteAssets.map((asset) => (
              <option key={asset.siteAssetId} value={asset.siteAssetId}>
                {asset.name} ({ASSET_TYPE_LABEL[asset.assetType]})
              </option>
            ))}
          </select>
        </label>
        <p className="setup__field-hint">
          {siteAssets.length === 0
            ? "No asset zones on this site yet - add them in the Asset zones section below to group checkpoints by building, parking, or gate."
            : "Groups this stop under a building, parking area, or gate on the Command Center map. Leave as None if it stands alone."}
        </p>
      </fieldset>
      <fieldset className="tpl__fieldset">
        <legend className="tpl__legend">How guards prove they were here</legend>
        <label className="tpl__field">
          Preferred verification method
          <select value={method} onChange={(event) => setMethod(event.target.value)}>
            {CHECKPOINT_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {methodHint !== null ? <p className="setup__field-hint">{methodHint}</p> : null}
      </fieldset>
      <p className="minter__confirm-note">
        {method === "gps_geofence"
          ? "Geofence radius is how close the patrol vehicle or officer must be (meters). Min GPS accuracy rejects weak phone fixes; min dwell seconds stops drive-past false positives."
          : method === "manual"
            ? "Manual check-ins rely on guard attestation. Geofence fields are optional guardrails if you still want a location proof."
            : "Geofence fields are optional for tag scans. Leave them blank to accept a scan anywhere the tag is read."}
      </p>
      <div className="tpl__field-row">
        <label className="tpl__field">
          Geofence radius (m)
          <input
            type="number"
            min="0"
            step="any"
            value={radius}
            onChange={(event) => setRadius(event.target.value)}
          />
        </label>
        <label className="tpl__field">
          Min GPS accuracy (m)
          <input
            type="number"
            min="0"
            step="any"
            value={accuracy}
            onChange={(event) => setAccuracy(event.target.value)}
          />
        </label>
        <label className="tpl__field">
          Min dwell (s)
          <input
            type="number"
            min="0"
            step="1"
            value={dwell}
            onChange={(event) => setDwell(event.target.value)}
          />
        </label>
      </div>
      <RouteStopRequirementField
        isRequired={isRequired}
        disabled={busy}
        onChange={setIsRequired}
      />
      {localFailure !== null || failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {localFailure ?? failure}
        </p>
      ) : null}
      <GlassDrawerFooter>
        <button type="button" className="builder__navbtn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" form={formId} className="minter__confirm-go" disabled={busy}>
          {dialog.kind === "create" ? "Create checkpoint" : "Save checkpoint"}
        </button>
      </GlassDrawerFooter>
    </form>
  );
}
